import { useState, useEffect, useCallback, useRef } from 'react';

const INACTIVITY_CONFIG = {
  // Supabase JWT expires after 60 minutes by default
  tokenLifetime: 60 * 60 * 1000, // 60 minutes in ms
  
  // Warn user at 55 minutes (5 minutes before expiry)
  warningThreshold: 55 * 60 * 1000,
  
  // Grace period after warning (5 minutes to respond)
  gracePeriod: 5 * 60 * 1000,
  
  // Activity events to track
  activityEvents: ['mousedown', 'keydown', 'scroll', 'touchstart']
};

export const useInactivityWarning = (
  isAuthenticated: boolean,
  onLogout: () => void,
  onRefreshSession: () => Promise<boolean>
) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(INACTIVITY_CONFIG.gracePeriod);
  
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    setTimeRemaining(INACTIVITY_CONFIG.gracePeriod);
    const startTime = Date.now();
    
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, INACTIVITY_CONFIG.gracePeriod - elapsed);
      setTimeRemaining(remaining);
      
      if (remaining <= 0) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      }
    }, 1000);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated) return;

    lastActivityRef.current = Date.now();
    clearTimers();
    setShowWarning(false);

    // Set timer to show warning
    warningTimerRef.current = setTimeout(() => {
      console.warn('⏰ Inactivity warning threshold reached');
      setShowWarning(true);
      startCountdown();

      // Set timer to logout after grace period
      logoutTimerRef.current = setTimeout(() => {
        console.warn('⏰ Grace period expired - logging out due to inactivity');
        onLogout();
        setShowWarning(false);
      }, INACTIVITY_CONFIG.gracePeriod);
    }, INACTIVITY_CONFIG.warningThreshold);
  }, [isAuthenticated, clearTimers, startCountdown, onLogout]);

  const handleStillHere = useCallback(async () => {
    console.log('👤 User confirmed they are still active');
    const success = await onRefreshSession();
    
    if (success) {
      clearTimers();
      setShowWarning(false);
      resetInactivityTimer();
    }
  }, [onRefreshSession, clearTimers, resetInactivityTimer]);

  const handleLogout = useCallback(() => {
    console.log('👤 User chose to logout from inactivity warning');
    clearTimers();
    setShowWarning(false);
    onLogout();
  }, [onLogout, clearTimers]);

  // Track user activity
  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setShowWarning(false);
      return;
    }

    const handleActivity = () => {
      if (!showWarning) {
        resetInactivityTimer();
      }
    };

    // Add event listeners for all activity types
    INACTIVITY_CONFIG.activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Start the initial timer
    resetInactivityTimer();

    return () => {
      INACTIVITY_CONFIG.activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [isAuthenticated, showWarning, resetInactivityTimer, clearTimers]);

  return {
    showWarning,
    timeRemaining,
    handleStillHere,
    handleLogout,
  };
};
